import type { KeyboardEvent, ReactNode } from 'react';
import { cn } from '../../../lib/utils';

interface AppBarPopoverItemProps {
  onClick: () => void;
  unread: boolean;
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  meta?: ReactNode;
  trailing?: ReactNode;
}

export function AppBarPopoverItem({
  onClick,
  unread,
  leading,
  title,
  subtitle,
  meta,
  trailing,
}: AppBarPopoverItemProps) {
  // Rendered as a role="button" div rather than a native <button> so the text
  // (title/body) stays selectable and copyable — browsers block text selection
  // inside a <button>. A plain click still navigates; a drag that leaves a text
  // selection does not (so highlighting to copy never triggers navigation).
  const activate = () => {
    if (window.getSelection()?.toString()) return;
    onClick();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={activate}
      onKeyDown={handleKeyDown}
      className={cn(
        'flex w-full cursor-pointer select-text flex-col items-start gap-0.5 border-b px-3 py-2 text-left last:border-b-0 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        unread && 'bg-accent/40',
      )}
    >
      <div className="flex w-full items-start gap-2">
        {unread && !leading && (
          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
        )}
        {leading && <div className="mt-0.5 shrink-0">{leading}</div>}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
              {title}
            </div>
            {trailing}
          </div>
          {subtitle && (
            <div className="line-clamp-2 text-xs text-muted-foreground">
              {subtitle}
            </div>
          )}
          {meta && (
            <div className="mt-0.5 text-[11px] text-muted-foreground">{meta}</div>
          )}
        </div>
      </div>
    </div>
  );
}
